import React, { useState, useEffect } from 'react';
import { X, Calendar, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { userApi } from '../services/api';

interface Analysis {
  _id: string;
  analysisType: string;
  theme: string;
  analysisResult: {
    markdownContent: string;
    confidence: number;
    analysisVersion: string;
  };
  originalImageUrl: string;
  createdAt: string;
  tags: string[];
  pointsUsed: number;
}

interface UserAnalysisHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userNickname: string;
}

const UserAnalysisHistoryModal: React.FC<UserAnalysisHistoryModalProps> = ({
  isOpen,
  onClose,
  userId,
  userNickname,
}) => {
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchAnalyses = async (page = 1) => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await userApi.getUserAnalyses(userId, page, 20);

      if (response.success) {
        setAnalyses(response.data.analyses);
        setPagination(response.data.pagination);
      } else {
        setError('분석 이력을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
      console.error('Analyses fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && userId) {
      fetchAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const handlePageChange = (page: number) => {
    fetchAnalyses(page);
  };

  const handleAnalysisClick = (analysis: Analysis) => {
    setSelectedAnalysis(analysis);
    setShowDetail(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 점수 색상 로직 제거됨

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{userNickname}님의 관상 분석 이력</h2>
            <p className="text-sm text-gray-500 mt-1">총 {pagination.totalCount}개의 분석 결과</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">로딩 중...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => fetchAnalyses()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                다시 시도
              </button>
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">분석 이력이 없습니다.</p>
            </div>
          ) : (
            <>
              {/* 분석 목록 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analyses.map((analysis) => (
                  <div
                    key={analysis._id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleAnalysisClick(analysis)}
                  >
                    {/* 이미지 */}
                    <div className="aspect-square mb-3 rounded-lg overflow-hidden bg-gray-100">
                      {analysis.originalImageUrl ? (
                        <img
                          src={analysis.originalImageUrl}
                          alt="관상 분석 이미지"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">이미지 없음</div>
                      )}
                    </div>

                    {/* 분석 정보 */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{analysis.analysisType}</span>
                        <span className="text-xs text-gray-500">{analysis.pointsUsed}P</span>
                      </div>

                      <p className="text-sm text-gray-600 line-clamp-2">분석 결과가 생성되었습니다.</p>

                      {/* 점수 섹션 제거됨 */}

                      {/* 태그 */}
                      {analysis.tags && analysis.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {analysis.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* 생성일 */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(analysis.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 페이지네이션 */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-center mt-6 space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <span className="px-3 py-2 text-sm text-gray-700">
                    {pagination.currentPage} / {pagination.totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* 상세 보기 모달 */}
        {showDetail && selectedAnalysis && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">분석 상세 정보</h3>
                <button
                  onClick={() => setShowDetail(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 이미지 */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">원본 이미지</h4>
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                      {selectedAnalysis.originalImageUrl ? (
                        <img
                          src={selectedAnalysis.originalImageUrl}
                          alt="관상 분석 이미지"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">이미지 없음</div>
                      )}
                    </div>
                  </div>

                  {/* 분석 결과 */}
                  <div className="space-y-4">
                    {/* 요약/성격 섹션 제거됨 */}

                    {/* 운세 점수 섹션 제거됨 */}

                    {/* 얼굴 특징 섹션 제거됨 */}

                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-2">분석 정보</h4>
                      <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">분석일:</span> {formatDate(selectedAnalysis.createdAt)}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">사용 포인트:</span> {selectedAnalysis.pointsUsed}P
                        </div>
                        {/* 조회/공유 수 제거됨 */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAnalysisHistoryModal;
